import React from 'react';
import { DIRECTION, deltaToneClass, formatValue, resolveDelta } from '../../lib/format';

/**
 * Delta chips and target lines — the two pieces of context that turn a number
 * into a reading.
 *
 * All of the judgement lives in `resolveDelta` (lib/format): whether the move is
 * good, whether there is enough history to claim a move at all, and how the
 * magnitude should be worded. This file only renders what it is told, which is
 * why a rising missed-call count can never come out green.
 */

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

const cx = (...parts) => parts.filter(Boolean).join(' ');

/**
 * Period-over-period movement.
 *
 * @param {object|null} delta      { value, base, window } straight from the API.
 * @param {string} direction       DIRECTION.* — decides the colour, not the sign.
 * @param {'rate'|'count'} basis   picks the volume floor below which a delta is suppressed.
 * @param {number|null} baseDenominator  size of the comparison period.
 * @param {string} format          the metric's format, so "up 4 pts" is not "up 4.8%".
 * @param {boolean} showWindow     render the "vs 12–18 Aug" line beneath the chip.
 */
export function Delta({
  delta,
  direction = DIRECTION.NEUTRAL,
  basis = 'count',
  baseDenominator = null,
  format,
  showWindow = true,
  className = '',
}) {
  const resolved = resolveDelta(delta, direction, { basis, baseDenominator, format });

  // 'none' means the server sent no comparable figure. A tile with a gap where
  // the delta would be is honest; a chip reading "0" would not be.
  if (!resolved || resolved.state === 'none') return null;

  // Too little history to compare against: say so in words, with no arrow and no
  // colour, so it cannot be mistaken for a measured flat period.
  if (resolved.state === 'insufficient') {
    return (
      <span className={cx('text-micro text-ink-500', className)} title={resolved.title || undefined}>
        {resolved.text}
      </span>
    );
  }

  const flat = resolved.state === 'flat';
  // resolveDelta words a flat move as "0 pts"; the phrase reads better and can't
  // be misread as a rounded-away movement.
  const magnitude = flat ? 'no change' : resolved.text;
  // The arrow is a glyph, so the direction is spelled out for screen readers.
  const spoken = flat ? '' : resolved.arrow === '▲' ? 'up ' : 'down ';
  const windowLine = showWindow && resolved.title ? resolved.title : null;

  return (
    <span className={cx('inline-flex flex-col items-start gap-0.5', className)}>
      <span
        className={cx(
          'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-micro font-medium',
          deltaToneClass(resolved.tone)
        )}
        // Only duplicate the window into a tooltip when it has no line of its own.
        title={windowLine ? undefined : resolved.title || undefined}
      >
        {resolved.arrow ? <span aria-hidden="true">{resolved.arrow}</span> : null}
        {spoken ? <span className="sr-only">{spoken}</span> : null}
        <span className={flat ? undefined : 'font-mono tabular-nums'}>{magnitude}</span>
      </span>
      {windowLine ? <span className="text-micro text-ink-500">{windowLine}</span> : null}
    </span>
  );
}

/**
 * A target is either a single value or a band — the AHT target is [min, max],
 * because "average handle time should be 4 minutes" is not a thing anyone means.
 * Returns null rather than an em dash: "target —" is noise, not information.
 */
const targetText = (value, format) => {
  if (Array.isArray(value)) {
    const [low, high] = value;
    if (!isNum(low) || !isNum(high)) return null;
    return `${formatValue(low, format)}–${formatValue(high, format)}`;
  }
  return isNum(value) ? formatValue(value, format) : null;
};

/**
 * "target 90%" under a metric.
 *
 * `source === 'default'` means an industry preset the tenant never chose, so it
 * is labelled as such — presenting a preset as their own commitment is how a
 * dashboard ends up being argued with instead of acted on.
 */
export function Target({ value, format = 'integer', source }) {
  const text = targetText(value, format);
  if (!text) return null;

  return (
    <span className="inline-flex items-baseline gap-1 text-micro text-ink-500">
      <span>target</span>
      <span className="font-mono tabular-nums">{text}</span>
      {source === 'default' ? (
        // A plain word, not an italic aside: the provenance of the number is a
        // fact about it, at the same weight as the number itself.
        <span
          className="rounded-sm bg-surface-2 px-1"
          title="Industry default - set your own target in Settings."
        >
          default
        </span>
      ) : null}
    </span>
  );
}
